import { useState, useRef } from 'react'
import { expeditedReviewConfig } from '@/config/expeditedReviewConfig'
import { submitExpeditedReview } from '@/services/intercom/intercomService'
import { GradientSlider } from '@/components/ui/PinkGradientSlider'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

interface ExpeditedReviewFormProps {
  applicationId: string
  applicantEmail: string
  applicantFirstName?: string | null
  applicantPhone?: string | null
  startTime?: number | null
  onBack: () => void
  onSubmitted: () => void
}

export function ExpeditedReviewForm({
  applicationId,
  applicantEmail,
  startTime,
  onBack,
  onSubmitted
}: ExpeditedReviewFormProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Form fields
  const [dob, setDob] = useState('')
  const [instagram, setInstagram] = useState('')
  const [twitter, setTwitter] = useState('')
  const [snapchat, setSnapchat] = useState('')
  const [zipCode, setZipCode] = useState('')
  const [profileFile, setProfileFile] = useState<File | null>(null)
  const [profilePreview, setProfilePreview] = useState<string | null>(null)
  const [openToMeetups, setOpenToMeetups] = useState<boolean | null>(null)
  const [hobbies, setHobbies] = useState('')
  const [tenThousandAnswer, setTenThousandAnswer] = useState('')
  const [bodyType, setBodyType] = useState('')
  const [heightFeet, setHeightFeet] = useState('')
  const [heightInches, setHeightInches] = useState(0)

  // Form state
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')

  const config = expeditedReviewConfig

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const validTypes = config.fields.profilePicture.acceptedTypes
    if (!validTypes.includes(file.type)) {
      setError('Please upload a JPG, PNG, WEBP, or GIF image')
      return
    }

    const maxSize = config.fields.profilePicture.maxSizeMB * 1024 * 1024
    if (file.size > maxSize) {
      setError(`Image must be under ${config.fields.profilePicture.maxSizeMB}MB`)
      return
    }

    setError('')
    setProfileFile(file)
    setProfilePreview(URL.createObjectURL(file))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Validate required fields
    if (!dob) {
      setError('Please enter your date of birth')
      return
    }
    // Mirrors the server-side gate in authRoutes so an under-18 gets told here
    // rather than after a round trip. The server is the real gate; this is only
    // so the message is immediate.
    {
      const birthDate = new Date(dob)
      if (isNaN(birthDate.getTime())) {
        setError('Please enter a valid date of birth')
        return
      }
      const today = new Date()
      let ageYears = today.getUTCFullYear() - birthDate.getUTCFullYear()
      const monthDiff = today.getUTCMonth() - birthDate.getUTCMonth()
      if (monthDiff < 0 || (monthDiff === 0 && today.getUTCDate() < birthDate.getUTCDate())) {
        ageYears--
      }
      if (birthDate.getTime() > today.getTime()) {
        setError('Please enter a valid date of birth')
        return
      }
      if (ageYears < 18) {
        setError('You must be 18 or older to apply.')
        return
      }
    }
    if (!zipCode.trim()) {
      setError('Please enter your zip code')
      return
    }
    if (!profileFile && !profilePreview) {
      setError('Please upload a profile picture')
      return
    }
    if (openToMeetups === null) {
      setError('Please indicate if you are open to meetups or travel')
      return
    }
    if (!hobbies.trim()) {
      setError('Please enter your hobbies')
      return
    }
    if (!tenThousandAnswer.trim()) {
      setError('Please answer the $10,000 question')
      return
    }
    if (!bodyType) {
      setError('Please select your body type')
      return
    }
    if (!heightFeet) {
      setError('Please select your height')
      return
    }

    setIsSubmitting(true)

    try {
      // Upload profile picture if provided
      let profilePictureUrl: string | null = null
      if (profileFile) {
        const formData = new FormData()
        formData.append('avatar', profileFile)
        formData.append('userId', applicationId)

        const uploadRes = await fetch(`${API_URL}/api/avatar/upload`, {
          method: 'POST',
          body: formData
        })

        if (uploadRes.ok) {
          const uploadResult = await uploadRes.json()
          profilePictureUrl = uploadResult.avatar?.s3_key || null
        }
      }

      // Calculate time taken from clicking "REQUEST FOR EXPEDITED REVIEW" to submit
      const timeTaken = startTime ? Math.round((Date.now() - startTime) / 1000) : null

      const result = await submitExpeditedReview({
        applicationId,
        email: applicantEmail,
        isCarynArmyMember: false,
        carynArmyUsername: '',
        dob,
        instagram,
        twitter,
        snapchat,
        zipCode,
        profilePictureUrl,
        openToMeetups: openToMeetups || false,
        hobbies,
        tenThousandAnswer,
        bodyType,
        heightFeet,
        heightInches,
        timeTaken
      })

      if (!result.success) {
        setError(result.error || 'Failed to submit. Please try again.')
        setIsSubmitting(false)
        return
      }

      onSubmitted()
    } catch (err) {
      console.error('[ExpeditedReview] Submit error:', err)
      setError('Something went wrong. Please try again.')
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen starry-background flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        {/* Back button */}
        <button
          type="button"
          onClick={onBack}
          className="mb-4 text-sm text-[#4AA3CC] hover:text-[#3B8DB3] flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {config.backButton.label}
        </button>

        <div className="bg-white rounded-lg shadow-lg p-6 overflow-hidden">
          <h2 className="text-xl font-bold text-gray-900 mb-1 text-center">
            {config.title}
          </h2>
          <p className="text-sm text-gray-500 mb-6 text-center">
            {config.subtitle}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Full application prompt */}
            <p className="text-sm text-gray-600 bg-[#F0F8FF] p-3 rounded-lg">
              {config.fullApplicationPrompt}
            </p>

            {/* DOB */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {config.fields.dob.label}
              </label>
              <input
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#4AA3CC] focus:border-[#4AA3CC] disabled:bg-gray-100"
              />
            </div>

            {/* Social Media Links */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Social Media Links
              </label>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 w-20">Instagram</span>
                  <input
                    type="text"
                    value={instagram}
                    onChange={(e) => setInstagram(e.target.value)}
                    placeholder={config.fields.instagram.placeholder}
                    disabled={isSubmitting}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#4AA3CC] focus:border-[#4AA3CC] disabled:bg-gray-100 text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 w-20">Twitter</span>
                  <input
                    type="text"
                    value={twitter}
                    onChange={(e) => setTwitter(e.target.value)}
                    placeholder={config.fields.twitter.placeholder}
                    disabled={isSubmitting}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#4AA3CC] focus:border-[#4AA3CC] disabled:bg-gray-100 text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 w-20">Snapchat</span>
                  <input
                    type="text"
                    value={snapchat}
                    onChange={(e) => setSnapchat(e.target.value)}
                    placeholder={config.fields.snapchat.placeholder}
                    disabled={isSubmitting}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#4AA3CC] focus:border-[#4AA3CC] disabled:bg-gray-100 text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Zip Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {config.fields.zipCode.label}
              </label>
              <input
                type="text"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                placeholder={config.fields.zipCode.placeholder}
                maxLength={config.fields.zipCode.maxLength}
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#4AA3CC] focus:border-[#4AA3CC] disabled:bg-gray-100"
              />
            </div>

            {/* Profile Picture Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {config.fields.profilePicture.label}
              </label>
              <div className="flex items-center gap-4">
                {profilePreview ? (
                  <div className="relative">
                    <img
                      src={profilePreview}
                      alt="Preview"
                      className="w-16 h-16 rounded-full object-cover border-2 border-[#87CEEB]"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setProfileFile(null)
                        setProfilePreview(null)
                        if (fileInputRef.current) fileInputRef.current.value = ''
                      }}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center"
                    >
                      x
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="w-16 h-16 rounded-full border-2 border-dashed border-[#87CEEB] flex items-center justify-center cursor-pointer hover:border-[#4AA3CC] hover:bg-[#F0F8FF] transition-colors"
                  >
                    <svg className="w-6 h-6 text-[#87CEEB]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSubmitting}
                  className="text-sm text-[#4AA3CC] hover:text-[#3B8DB3] font-medium"
                >
                  {profilePreview ? 'Change Photo' : 'Choose Photo'}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>
            </div>

            {/* Open to Meetups */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {config.fields.meetups.label}
              </label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setOpenToMeetups(true)}
                  className={`flex-1 py-2 rounded-lg font-medium text-sm transition-colors ${
                    openToMeetups === true
                      ? 'bg-gradient-to-r from-[#3B8DB3] to-[#6CB4D9] text-white'
                      : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setOpenToMeetups(false)}
                  className={`flex-1 py-2 rounded-lg font-medium text-sm transition-colors ${
                    openToMeetups === false
                      ? 'bg-gradient-to-r from-[#3B8DB3] to-[#6CB4D9] text-white'
                      : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  No
                </button>
              </div>
            </div>

            {/* Hobbies */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {config.fields.hobbies.label}
              </label>
              <textarea
                value={hobbies}
                onChange={(e) => setHobbies(e.target.value)}
                placeholder={config.fields.hobbies.placeholder}
                rows={config.fields.hobbies.rows}
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#4AA3CC] focus:border-[#4AA3CC] resize-none disabled:bg-gray-100"
              />
            </div>

            {/* $10,000 Question */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {config.fields.tenThousandQuestion.label}
              </label>
              <textarea
                value={tenThousandAnswer}
                onChange={(e) => setTenThousandAnswer(e.target.value)}
                placeholder={config.fields.tenThousandQuestion.placeholder}
                rows={config.fields.tenThousandQuestion.rows}
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#4AA3CC] focus:border-[#4AA3CC] resize-none disabled:bg-gray-100"
              />
            </div>

            {/* Body Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {config.fields.bodyType.label}
              </label>
              <div className="flex flex-wrap gap-2">
                {config.fields.bodyType.options.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setBodyType(option)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      bodyType === option
                        ? 'bg-gradient-to-r from-[#3B8DB3] to-[#6CB4D9] text-white'
                        : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>

            {/* Height (Feet) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {config.fields.heightFeet.label}
              </label>
              <select
                value={heightFeet}
                onChange={(e) => setHeightFeet(e.target.value)}
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#4AA3CC] focus:border-[#4AA3CC] disabled:bg-gray-100 bg-white"
              >
                <option value="">Select height...</option>
                {config.fields.heightFeet.options.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            {/* Height Inches - Gradient Slider */}
            <GradientSlider
              label={config.fields.heightInches.label}
              min={config.fields.heightInches.min}
              max={config.fields.heightInches.max}
              step={config.fields.heightInches.step}
              value={heightInches}
              onChange={setHeightInches}
              formatValue={(v) => `${v.toFixed(1)}"`}
            />

            {/* Error */}
            {error && (
              <div className="text-red-600 text-sm">{error}</div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 bg-gradient-to-r from-[#3B8DB3] to-[#6CB4D9] text-white rounded-lg font-bold uppercase tracking-wide hover:from-[#2C7799] hover:to-[#4AA3CC] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? config.submitButton.loadingLabel : config.submitButton.label}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
